import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Divider,
} from '@nextui-org/react';
import {
  MarketDataCommissions,
  DEFAULT_MARKET_DATA_COMMISSIONS,
} from 'common/src/api/presettlement-settings';
import { useTranslation } from '../hooks/useTranslation';

interface MarketCommissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (commissions: MarketDataCommissions) => Promise<boolean>;
  initialValues?: MarketDataCommissions;
}

const MarketCommissionsModal: React.FC<MarketCommissionsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const { t } = useTranslation('market');
  const [commissions, setCommissions] = useState<MarketDataCommissions>(
    initialValues || DEFAULT_MARKET_DATA_COMMISSIONS
  );
  const [loading, setLoading] = useState(false);

  // Update commissions when initialValues change (e.g., species change)
  useEffect(() => {
    if (initialValues) {
      setCommissions(initialValues);
    }
  }, [initialValues]);

  const handleChange = (field: keyof MarketDataCommissions, value: string) => {
    const numValue = parseFloat(value) || 0;
    setCommissions((prev) => ({ ...prev, [field]: numValue }));
  };

  const handleSave = async () => {
    setLoading(true);
    const success = await onSave(commissions);
    setLoading(false);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='3xl'
      scrollBehavior='inside'
      isDismissable={false}
      classNames={{
        base: 'max-h-[90vh]',
      }}
    >
      <ModalContent className='flex flex-col max-h-[90vh]'>
        <ModalHeader className='flex flex-col gap-1 flex-shrink-0'>
          <h3 className='text-xl font-bold'>
            {t('Configuración de Comisiones y Costos')}
          </h3>
          <p className='text-sm text-default-400 font-normal'>
            {t('Configure los porcentajes y costos para calcular Retorno FOB y Retorno Productor')}
          </p>
        </ModalHeader>
        <ModalBody className='flex-1 overflow-y-auto'>
          <div className='space-y-6'>
            {/* Sección: Cálculo FOB */}
            <div>
              <h4 className='text-lg font-semibold mb-4 '>
                {t('Para calcular Retorno FOB')}
              </h4>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Input
                  type='number'
                  label={t('Comisión Recibidor (%)')}
                  value={commissions.receiver_commission.toString()}
                  onChange={(e) =>
                    handleChange('receiver_commission', e.target.value)
                  }
                  endContent={<span className='text-default-300'>%</span>}
                  description={t('Porcentaje sobre ventas')}
                />
                <Input
                  type='number'
                  label={t('VAT / Tax (%)')}
                  value={commissions.vat_tax.toString()}
                  onChange={(e) => handleChange('vat_tax', e.target.value)}
                  endContent={<span className='text-default-300'>%</span>}
                  description={t('Porcentaje sobre ventas')}
                />
                <Input
                  type='number'
                  label={t('Costo Flete Aéreo (USD/Kg)')}
                  value={commissions.air_freight_cost.toString()}
                  onChange={(e) =>
                    handleChange('air_freight_cost', e.target.value)
                  }
                  startContent={<span className='text-default-300'>$</span>}
                  endContent={<span className='text-default-300'>/Kg</span>}
                  description={t('Tarifa sugerida: USD 7.0/Kg')}
                />
                <Input
                  type='number'
                  label={t('Costo Flete Marítimo (USD/Kg)')}
                  value={commissions.ocean_freight_cost.toString()}
                  onChange={(e) =>
                    handleChange('ocean_freight_cost', e.target.value)
                  }
                  startContent={<span className='text-default-300'>$</span>}
                  endContent={<span className='text-default-300'>/Kg</span>}
                  description={t('Tarifa sugerida: USD 0.6/Kg')}
                />
                <Input
                  type='number'
                  label={t('Otros Costos Aéreo (USD/Kg)')}
                  value={commissions.air_other_costs.toString()}
                  onChange={(e) =>
                    handleChange('air_other_costs', e.target.value)
                  }
                  startContent={<span className='text-default-300'>$</span>}
                  endContent={<span className='text-default-300'>/Kg</span>}
                  description={t('Tarifa sugerida: USD 0.5/Kg')}
                />
                <Input
                  type='number'
                  label={t('Otros Costos Marítimo (USD/Kg)')}
                  value={commissions.ocean_other_costs.toString()}
                  onChange={(e) =>
                    handleChange('ocean_other_costs', e.target.value)
                  }
                  startContent={<span className='text-default-300'>$</span>}
                  endContent={<span className='text-default-300'>/Kg</span>}
                  description={t('Tarifa sugerida: USD 0.15/Kg')}
                />
              </div>
            </div>

            <Divider />

            {/* Sección: Cálculo Retorno Productor */}
            <div>
              <h4 className='text-lg font-semibold mb-4 '>
                {t('Para calcular Retorno Productor')}
              </h4>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Input
                  type='number'
                  label={t('Comisión Exportadora (%)')}
                  value={commissions.exporter_commission.toString()}
                  onChange={(e) =>
                    handleChange('exporter_commission', e.target.value)
                  }
                  endContent={<span className='text-default-300'>%</span>}
                  description={t('Porcentaje sobre resultado FOB')}
                />
                <Input
                  type='number'
                  label={t('Costo Embalaje (USD/Kg)')}
                  value={commissions.packing_cost.toString()}
                  onChange={(e) => handleChange('packing_cost', e.target.value)}
                  startContent={<span className='text-default-300'>$</span>}
                  endContent={<span className='text-default-300'>/Kg</span>}
                  description={t('Mismo valor para aéreo y marítimo. Sugerido: USD 2.0/Kg')}
                />
              </div>
            </div>

            {/* Información de ayuda */}
            <div className='bg-blue-50 dark:bg-blue-950 p-4 rounded-lg'>
              <h5 className='font-semibold text-blue-900 mb-2'>
                {t('ℹ️ Cómo se calculan los precios')}
              </h5>
              <div className='text-sm text-blue-800 space-y-1'>
                <p>
                  <strong>{t('Retorno FOB')}:</strong> {t('Precio Market - Comisión Recibidor - VAT - Costo Flete - Otros Costos')}
                </p>
                <p>
                  <strong>{t('Retorno Productor')}:</strong> {t('Retorno FOB - Comisión Exportadora - Costo Embalaje')}
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className='flex-shrink-0 border-t bg-background'>
          <Button variant='light' onPress={onClose}>
            {t('Cancelar')}
          </Button>
          <Button
            onPress={handleSave}
            isLoading={loading}
            variant='solid'
            color='primary'
          >
            {t('Guardar Configuración')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MarketCommissionsModal;
